import type { AppConfig, ResolvedRouteRegistryEntry } from '../types';

/**
 * Extracts base paths from an app registry that have `triggerPageRefreshOnNavigateAway: true`.
 * These are apps that should trigger a full page refresh when navigating away from them.
 *
 * @param appRegistry - Array of app configurations
 * @returns Array of base paths (e.g., ['/asset-management'])
 */
export function extractRefreshBasePathsFromAppRegistry(
  appRegistry: readonly AppConfig[] | undefined
): readonly string[] {
  if (!appRegistry) {
    return [];
  }
  return appRegistry
    .filter((app) => app.triggerPageRefreshOnNavigateAway === true)
    .map((app) => app.basePath);
}

/**
 * Extracts base paths from a route registry that have `triggerPageRefreshOnNavigateAway: true`.
 * These are routes that should trigger a full page refresh when navigating away from them.
 *
 * @param routeRegistry - Array of route configurations
 * @returns Array of base paths (e.g., ['/profile'])
 */
export function extractRefreshBasePathsFromRouteRegistry(
  routeRegistry: readonly ResolvedRouteRegistryEntry[] | undefined
): readonly string[] {
  if (!routeRegistry) {
    return [];
  }
  return routeRegistry
    .filter((route) => route.triggerPageRefreshOnNavigateAway === true)
    .map((route) => route.basePath);
}

/**
 * Combines app and route registry refresh base paths into a single array.
 * Used to configure which paths should trigger page refresh on navigation away.
 *
 * @param appRegistry - App registry entries
 * @param routeRegistry - Route registry entries
 * @returns Combined array of refresh base paths
 */
export function getAllRefreshBasePaths(
  appRegistry: readonly AppConfig[] | undefined,
  routeRegistry: readonly ResolvedRouteRegistryEntry[] | undefined
): readonly string[] {
  return [
    ...extractRefreshBasePathsFromAppRegistry(appRegistry),
    ...extractRefreshBasePathsFromRouteRegistry(routeRegistry),
  ];
}
